import React, { useMemo } from "react";
import { FormBuilderItemView } from "../item/FormBuilderItemView";
import classNames from "classnames";
import { useFormBuilderEditorContext } from "../../../../formBuilderEditorContext";
import { useFormBuilderEntityDomNodeRefMapRegistration } from "../../../../hooks/entityDomNodeRefMap/useFormBuilderEntityDomNodeRefMapRegistration";
import { TFormBuilderEntityViewMoveContext, IUseFormBuilderEntityViewMoveArgs, useFormBuilderEntityViewMove } from "../../../../hooks/entityViewMove/useFormBuilderEntityViewMove";
import { formBuilderGenerateRowDesignStyles, IFormBuilderRow } from "../../../../model/formBuilderRow";

interface IFormBuilderRowViewProps {
  row: IFormBuilderRow;
}

interface IFormBuilderRowViewBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const ROW_MOVE_OPACITY = 0.7;
const ROW_MOVE_Z_INDEX = 99;

export function FormBuilderRowView(props: IFormBuilderRowViewProps) {
  const { row } = props;

  const { formBuilderController, formBuilderEntityDomNodeRefMap } = useFormBuilderEditorContext();

  const domNodeRefMapRegistration = useFormBuilderEntityDomNodeRefMapRegistration(row.key, formBuilderEntityDomNodeRefMap);

  const designStyles = formBuilderGenerateRowDesignStyles(row);

  const selected = formBuilderController.selectedEntity?.type === "row" && formBuilderController.selectedEntity.key === row.key;
  const childSelected = formBuilderController.selectedEntity?.type === "item" && formBuilderController.selectedEntity.rowKey === row.key;

  const moving = selected && formBuilderController.selectedEntity.editingMode.type === "move";

  const moveContext: TFormBuilderEntityViewMoveContext = useMemo(() => ({
    type: "row",
    entity: row,
  }), [row]);

  const viewMoveArgs: IUseFormBuilderEntityViewMoveArgs = useMemo(
    () => ({
      context: moveContext,
      moving,
    }),
    [
      moveContext,
      moving,
    ],
  );

  const { viewMovePosition, viewMoveDimensions, handleViewMovePointerDown } = useFormBuilderEntityViewMove(viewMoveArgs);

  function getBoundingBox(): IFormBuilderRowViewBoundingBox {
    if (moving && viewMovePosition != null && viewMoveDimensions != null) {
      return {
        x: viewMovePosition.x,
        y: viewMovePosition.y,
        width: viewMoveDimensions.width,
        height: viewMoveDimensions.height,
      };
    } else {
      return null;
    }
  }

  const boundingBox = getBoundingBox();

  const detached = moving && boundingBox != null;

  return (
    <>
      <div
        ref={domNodeRefMapRegistration}
        className={classNames(designStyles.className, "builder-row", {
          "builder-row-selected": selected,
        })}
        style={{
          ...designStyles.styleProperties,
          ...(detached ? {
            position: "absolute",
            left: boundingBox.x,
            top: boundingBox.y,
            width: boundingBox.width,
            height: boundingBox.height,
            opacity: ROW_MOVE_OPACITY,
            zIndex: ROW_MOVE_Z_INDEX,
          } : {}),
        }}
        onClick={e => {
          e.stopPropagation();
      
          if (!selected && !childSelected) {
            formBuilderController.selectEntity({
              selectedEntity: {
                type: "row",
                key: row.key,
                editingMode: {
                  type: "none",
                },
              },
            });
          }
        }}
        onPointerDown={e => {
          if (selected) {
            handleViewMovePointerDown(e);
          }
        }}
      >
        {row.children.map(item => {
          return (
            <FormBuilderItemView
              key={item.key}
              row={row}
              item={item}
            />
          );
        })}
      </div>
      
      {detached && (
        <div
          style={{
            visibility: "hidden",
            width: boundingBox.width,
            height: boundingBox.height,
          }}
        />
      )}
    </>
  );
}
